import { createI18n } from 'vue-i18n'
import { numberFormats } from '../i18n/number-formats'
import { datetimeFormats } from '../i18n/datetime-formats'
import { ref } from 'vue'

export const Locales = [
  { code: 'en', file: 'en-US.ts', name: 'English' },
  { code: 'fr', file: 'fr-FR.ts', name: 'Français' },
  { code: 'de', file: 'de-DE.ts', name: 'Deutsch' },
  { code: 'nl', file: 'nl-NL.ts', name: 'Nederlands' },
  { code: 'pl', file: 'pl-PL.ts', name: 'Polski' },
  { code: 'es', file: 'es-ES.ts', name: 'Español' },
  { code: 'ca', file: 'es-CA.ts', name: 'Català' },
  { code: 'it', file: 'it-IT.ts', name: 'Italien' },
  { code: 'he', file: 'he-IL.ts', name: 'Hebrew' },
  { code: 'pt', file: 'pt-PT.ts', name: 'Português' }
] as const

export type LocaleCode = (typeof Locales)[number]['code']
type Messages = Record<string, string>

const localesFiles = import.meta.glob('../i18n/locales/*.ts', { eager: false })

const DefaultLocale: LocaleCode = 'en'
export const LocaleCodes = Locales.map((l) => l.code)

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DefaultLocale,
  supportedLocales: LocaleCodes,
  fallbackLocale: DefaultLocale,
  escapeParameterHtml: false,
  warnHtmlMessage: false,
  numberFormats,
  datetimeFormats,
  messages: {}
})

const loadedLanguages = ref<string[]>([])

async function loadLocaleMessages(locale: LocaleCode) {
  const localeFile = Locales.find((l) => l.code === locale)?.file

  if (loadedLanguages.value.includes(locale)) return locale

  const path = `../i18n/locales/${localeFile}`
  const messages = (await localesFiles[path]()) as { default: Messages } | Messages

  i18n.global.setLocaleMessage(locale, messages.default || messages)
  loadedLanguages.value.push(locale)
  return locale
}

export async function setI18nLanguage(locale: LocaleCode) {
  await loadLocaleMessages(locale)
  i18n.global.locale.value = locale
}

export async function initI18n() {
  const browserLocale = navigator.language.split('-')[0]
  const cookieLocale = document.cookie.match(/locale=(\w+)/)?.[1]

  const locale = cookieLocale || (LocaleCodes.includes(browserLocale as LocaleCode) ? browserLocale : DefaultLocale)

  await setI18nLanguage(locale as LocaleCode)

  // cross origin cookie
  document.cookie = `locale=${locale}; path=/; SameSite=None; Secure`
}

export default defineNuxtPlugin(({ vueApp }) => {
  initI18n()

  vueApp.use(i18n)

  return {
    provide: {
      i18n
    }
  }
})
